import React, {useEffect, useState} from "react";
import {Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import * as paymentTerminalActions from "../../../lib/payment-terminal/actions";
import {useDispatch, useSelector} from "react-redux";
import {ApiResponse} from "apisauce";
import api from "../../../Services/api";
import {RootState} from "../../../Redux";
import PaymentActions from '../../../Redux/paymentRedux'
import PaymentHeaders from "./PaymentHeaders";
import PrintReceipt from "../Common/PrintReceipt";
import BackButton from "../Common/BackButton";
import {ServiceCardType} from "../Types";
import {useTranslation} from "react-i18next";
import {getLocale, numberToCurrencyString} from "../../../Utils/helpers";
import {LoginCredentialsType} from "../../../Utils/types";
import {useApiKey} from "../../../Utils/hooks";
import selfServiceRedux from "../../../Redux/selfServiceRedux";

const { getCheckIn } = api.create();

interface PayWithCardProps {
    serviceCard: ServiceCardType;
    setServiceCard: (serviceCard: ServiceCardType) => void
    setPaymentMade: (paymentMade: boolean) => void;
    onBack: () => void;
}

export const PayWithCard = ({ serviceCard, setServiceCard, setPaymentMade, onBack }: PayWithCardProps) => {
    const cardPayment = useSelector((state: RootState) => state.payment.cardPayment)
    const cardPaymentCompletedStatus = useSelector((state: RootState) => state.payment.cardPaymentStatus.isCompleted)
    const terminalDisplayMessage = useSelector((state: RootState) => state.payment.cardPaymentStatus.displayMessage)
    const fetching = useSelector((state: RootState) => state.payment.fetching.payment)
    const [cardPaymentCompleted, setCardPaymentCompleted] = useState<boolean>(cardPaymentCompletedStatus)
    const [printReceipt, setPrintReceipt] = useState<boolean>(false)
    const [connectionInitialized, setConnectionInitialized] = useState<boolean>(false)
    const [paymentInitialized, setPaymentInitialized] = useState<boolean>(false)
    const [terminalStatusMessage, setTerminalStatusMessage] = useState<string|null>(null)
    const [abortRequested, setAbortRequested] = useState<boolean>(false)

    const dispatch = useDispatch();
    const { t } = useTranslation()
    const language = getLocale()

    const apiKey: LoginCredentialsType = useApiKey();
    const paymentTerminalApiUrl = useSelector((state: RootState) => state.ui.settings.selfService.paymentTerminalApiUrl)
    const apiUrl = `${paymentTerminalApiUrl}/?k=${apiKey.apiKey}&n=${apiKey.systemName}`

    const connectionInfo = useSelector((state: RootState) => state.paymentTerminal)

    const handleGoBack = () => {
        if (connectionInfo.connection.connected || !connectionInitialized) {
            dispatch(PaymentActions.abortCardPayment())
        }
        setAbortRequested(true)
    }

    useEffect(() => {
        dispatch(selfServiceRedux.setAllowClose(false))
    }, [dispatch]);

    useEffect(() => {
        setCardPaymentCompleted(status => status || cardPaymentCompletedStatus)
    }, [cardPaymentCompletedStatus, setCardPaymentCompleted]);

    useEffect(() => {
        if (paymentInitialized && (cardPaymentCompleted || cardPaymentCompletedStatus) && cardPayment?.isPaid) {
            setPrintReceipt(true)
        }
    }, [cardPayment, cardPaymentCompleted, cardPaymentCompletedStatus, paymentInitialized]);

    useEffect(() => {
        if (connectionInitialized && !paymentInitialized && !abortRequested) {
            dispatch(PaymentActions.createCardPayment(serviceCard.token, language))
            setPaymentInitialized(true)
        }
    }, [
        abortRequested,
        connectionInitialized,
        paymentInitialized,
        dispatch,
        language,
        serviceCard.token,
    ]);

    useEffect(() => {
        if (connectionInfo.info.terminalId) {
            setConnectionInitialized(true)
        }
    }, [connectionInfo.info.terminalId, connectionInfo.connection.connected, connectionInfo.connection.connecting, connectionInfo.status.readyForTransaction, connectionInfo.status.readyForTransaction, connectionInfo.status.transactionStatus])

    useEffect(() => {
        setAbortRequested(false)
        setConnectionInitialized(false)
        setPaymentInitialized(false)
        dispatch(paymentTerminalActions.configure({apiUrl: apiUrl, aliveInterval: 10}))
        dispatch(paymentTerminalActions.connect())
    }, [apiUrl, dispatch]);

    useEffect(() => {
        if (abortRequested && !connectionInfo.connection.connected) {
            dispatch(paymentTerminalActions.disconnect())
            dispatch(selfServiceRedux.setAllowClose(true))
            setAbortRequested(false)
            onBack()
        }
    }, [
        abortRequested,
        onBack,
        connectionInfo.connection.connected,
        dispatch,
    ]);

    useEffect(() => {
        if (paymentInitialized) {
            if (!cardPaymentCompleted && !cardPaymentCompletedStatus && connectionInfo.connection.connected === false && connectionInfo.connection.connecting === false) {
                setTerminalStatusMessage(message => message ? t('selfService.terminalNotConnected') : null)
            } else if (terminalDisplayMessage) {
                setTerminalStatusMessage(terminalDisplayMessage);
            } else if (connectionInfo.status.transactionStatus !== null) {
                setTerminalStatusMessage(terminalDisplayMessage ?? t('selfService.followPaymentTerminalInstructions'))
            } else if (cardPaymentCompleted || cardPaymentCompletedStatus) {
                if (cardPayment?.isPaid !== undefined) {
                    setTerminalStatusMessage(cardPayment.isPaid ? t('selfService.paymentSucceeded') : t('errors.paymentFailed'))
                }
            } else {
                setTerminalStatusMessage(terminalDisplayMessage ?? t('selfService.waitingForPaymentTerminal'))
            }
        } else if (connectionInfo.connection.connected || connectionInfo.connection.connecting) {
            setTerminalStatusMessage(terminalDisplayMessage ?? t('selfService.waitingForPaymentTerminal'))
        } else if (!connectionInitialized) {
            setTerminalStatusMessage(message => message ? t('selfService.terminalNotConnected') : null)
        }
    }, [
        cardPaymentCompletedStatus,
        cardPaymentCompleted,
        cardPayment?.isPaid,
        connectionInfo.transaction,
        connectionInfo.status.transactionStatus,
        connectionInfo.connection.connecting,
        connectionInfo.connection.connected,
        connectionInitialized,
        paymentInitialized,
        terminalDisplayMessage,
        t
    ]);

    const onPrintDone = async ()=> {
        const checkInResponse: ApiResponse<any> = await getCheckIn(serviceCard.token)
        if (checkInResponse.ok) {
            const updatedServiceCard = checkInResponse.data as ServiceCardType;
            if (updatedServiceCard.sale.remainingPrice.vatIncluded === 0) {
                setServiceCard(updatedServiceCard)
            }
        }
        dispatch(selfServiceRedux.setAllowClose(true))
        setPaymentMade(true)
    }

    return (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className={'pageMainContainer'}>
            <Grid className="pageMainHeader">
                <PaymentHeaders serviceCard={serviceCard} />
            </Grid>
            <Grid textAlign="center">
                <Typography variant="h5" style={{marginBottom: '1em'}}>
                    {serviceCard.sale.tasks[0].basket.items[0].name} {cardPayment ? numberToCurrencyString(cardPayment.amount?.value ?? serviceCard.sale.remainingPrice?.vatIncluded, 'EUR', 0) : null}
                </Typography>
                {abortRequested ?
                    <Typography variant="subtitle1" gutterBottom>{t('selfService.paymentAbortRequested')}</Typography>
                    :
                    <Typography variant="subtitle1" gutterBottom>{terminalStatusMessage ?? t('selfService.waitingForPaymentTerminal')}</Typography>
                }
            </Grid>
            <Grid/>
            <Grid>
                {printReceipt === true &&
                    <PrintReceipt serviceCard={serviceCard} type="receipt" onPrintDone={onPrintDone} />
                }
            </Grid>
            <Grid className="footerButtons">
                <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                    <BackButton disabled={connectionInitialized && (fetching || !paymentInitialized)} onBack={handleGoBack} />
                </Grid>
            </Grid>
        </Grid>
    )
}
